import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authSetAPI, planAPI, branchAPI } from '../../services/api';
import { useAuthStore, useBranchStore } from '../../store';
import { toast } from 'react-toastify';
import './AuthSets.css';

// The login methods a set gives the plan of at sign-in (backend services/authSet.service):
// a voucher signs in with its own batch's plan and a MAC with its device's, so a set
// cannot change those.
const AUTH_METHODS = [
    { value: 'pms',              label: 'PMS Login',         icon: 'fa-hotel' },
    { value: 'sms_otp',          label: 'SMS OTP',           icon: 'fa-sms' },
    { value: 'whatsapp_otp',     label: 'WhatsApp OTP',      icon: 'fa-whatsapp' },
];
// Older sets may name others: shown so they can be taken off, never applied.
const OTHER_METHOD_LABELS = {
    voucher: 'Voucher', access_code: 'Access Code', username_password: 'Username & Password',
    email_otp: 'Email OTP', qr: 'QR Login', mac: 'MAC Auth', social: 'Social Login',
};
const methodLabel = (m) => AUTH_METHODS.find((x) => x.value === m)?.label || OTHER_METHOD_LABELS[m] || m;

// What the guest gets is the plan's: its speed, devices, data and validity.
const speed = (kbps) => (!kbps ? '—' : kbps >= 1024 ? `${+(kbps / 1024).toFixed(1)} Mbps` : `${kbps} kbps`);
const planSpeed = (p) => (p?.bandwidth ? `↓${speed(p.bandwidth.downloadSpeed)} ↑${speed(p.bandwidth.uploadSpeed)}` : '—');
const planData = (p) => (p?.dataLimit?.total ? `${p.dataLimit.total} MB` : 'Unlimited');
const planValidity = (p) => (p?.validity?.duration ? `${p.validity.duration} ${p.validity.unit}` : '—');

const DAYS = ['mon','tue','wed','thu','fri','sat','sun'];

const EMPTY_FORM = {
    name: '', description: '',
    authMethods: [],
    planId: '',
    bandwidth: { upload: '', download: '', burstUpload: '', burstDownload: '' },
    sessionTimeout: '', idleTimeout: '', deviceLimit: 1, dataLimit: '',
    vlanId: '', ipPool: '', dnsProfile: '', firewallPolicy: '',
    landingPage: '', redirectUrl: '',
    accessSchedule: { enabled: false, days: [], startTime: '00:00', endTime: '23:59', timezone: 'Asia/Kolkata' },
    targetUserTypes: '',
    status: 'active', priority: 0,
    branchId: '',
};

export default function AuthSets() {
    const { admin } = useAuthStore();
    const { selectedBranch } = useBranchStore();
    const branchId = selectedBranch?._id;
    const qc = useQueryClient();
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [tab, setTab] = useState('basic');
    const [filterStatus, setFilterStatus] = useState('');
    const [search, setSearch] = useState('');

    const { data: setsRes, isLoading } = useQuery({
        queryKey: ['auth-sets', filterStatus, branchId],
        queryFn: () => authSetAPI.getAll({ ...(filterStatus ? { status: filterStatus } : {}), ...(branchId ? { branchId } : {}) }),
    });
    const sets = setsRes?.data?.data || [];

    const { data: plansRes } = useQuery({
        queryKey: ['plans-for-authsets', branchId],
        queryFn: () => planAPI.getAll({ limit: 200, ...(branchId ? { branchId } : {}) }),
    });
    const branchPlans = plansRes?.data?.data?.plans || plansRes?.data?.data || plansRes?.data || [];
    // The set's own plan is always a choice — an all-branches set's may be another branch's.
    const plans = editing?.planId?._id && !branchPlans.some((p) => p._id === editing.planId._id)
        ? [editing.planId, ...branchPlans]
        : branchPlans;

    const { data: branchesRes } = useQuery({
        queryKey: ['branches-for-authsets'],
        queryFn: () => branchAPI.getAll(),
        enabled: admin?.role === 'superadmin' || admin?.role === 'tenant_admin',
    });
    const branches = branchesRes?.data?.data?.branches || branchesRes?.data?.data || branchesRes?.data || [];

    const saveMutation = useMutation({
        mutationFn: (data) => editing ? authSetAPI.update(editing._id, data) : authSetAPI.create(data),
        onSuccess: () => {
            toast.success(editing ? 'Authorization set updated' : 'Authorization set created');
            qc.invalidateQueries({ queryKey: ['auth-sets'] });
            closeModal();
        },
        onError: (err) => toast.error(err.response?.data?.message || 'Save failed'),
    });

    const deleteMutation = useMutation({
        mutationFn: authSetAPI.delete,
        onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['auth-sets'] }); },
        onError: () => toast.error('Delete failed'),
    });

    const dupMutation = useMutation({
        mutationFn: authSetAPI.duplicate,
        onSuccess: () => { toast.success('Duplicated'); qc.invalidateQueries({ queryKey: ['auth-sets'] }); },
        onError: () => toast.error('Duplicate failed'),
    });

    const openCreate = () => { setEditing(null); setForm({ ...EMPTY_FORM, branchId: branchId || '' }); setTab('basic'); setShowModal(true); };
    const openEdit = (s) => {
        setEditing(s);
        setForm({
            ...EMPTY_FORM, ...s,
            planId: s.planId?._id || s.planId || '',
            branchId: s.branchId?._id || s.branchId || '',
            bandwidth: { upload: s.bandwidth?.upload || '', download: s.bandwidth?.download || '', burstUpload: s.bandwidth?.burstUpload || '', burstDownload: s.bandwidth?.burstDownload || '' },
            targetUserTypes: (s.targetUserTypes || []).join(', '),
            accessSchedule: s.accessSchedule || EMPTY_FORM.accessSchedule,
        });
        setTab('basic');
        setShowModal(true);
    };
    const closeModal = () => { setShowModal(false); setEditing(null); };

    const setF = (key, val) => setForm(f => ({ ...f, [key]: val }));
    const toggleMethod = (m) => setF('authMethods', form.authMethods.includes(m) ? form.authMethods.filter(x => x !== m) : [...form.authMethods, m]);
    const toggleDay = (d) => setForm(f => ({ ...f, accessSchedule: { ...f.accessSchedule, days: f.accessSchedule.days.includes(d) ? f.accessSchedule.days.filter(x => x !== d) : [...f.accessSchedule.days, d] } }));

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            ...form,
            bandwidth: { upload: +form.bandwidth.upload || undefined, download: +form.bandwidth.download || undefined, burstUpload: +form.bandwidth.burstUpload || undefined, burstDownload: +form.bandwidth.burstDownload || undefined },
            sessionTimeout: +form.sessionTimeout || undefined,
            idleTimeout: +form.idleTimeout || undefined,
            deviceLimit: +form.deviceLimit || 1,
            dataLimit: +form.dataLimit || undefined,
            vlanId: +form.vlanId || undefined,
            priority: +form.priority || 0,
            targetUserTypes: form.targetUserTypes ? form.targetUserTypes.split(',').map(s => s.trim()).filter(Boolean) : [],
            planId: form.planId || undefined,
            branchId: form.branchId || undefined,
        };
        saveMutation.mutate(payload);
    };

    const filtered = sets.filter(s =>
        !search || s.name.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="page-container-modern">
            <div className="page-header-modern">
                <div>
                    <h1 className="page-title-modern">Authorization Sets</h1>
                    <p className="page-subtitle-modern">The plan a guest gets for how they sign in — by branch, priority, user type and schedule</p>
                </div>
                <button className="btn-primary-modern" onClick={openCreate}>
                    <i className="fas fa-plus mr-2"></i> New Authorization Set
                </button>
            </div>

            {/* Filters */}
            <div className="filters-row-modern">
                <div className="search-box-modern">
                    <i className="fas fa-search"></i>
                    <input placeholder="Search sets…" value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <select className="filter-select-modern" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                </select>
            </div>

            {/* Stats bar */}
            <div className="stats-bar" style={{ display:'flex', gap:'1rem', marginBottom:'1.5rem', flexWrap:'wrap' }}>
                {[
                    { label:'Total', val: sets.length, color:'#6366f1' },
                    { label:'Active', val: sets.filter(s=>s.status==='active').length, color:'#22c55e' },
                    { label:'Inactive', val: sets.filter(s=>s.status==='inactive').length, color:'#ef4444' },
                ].map(s=>(
                    <div key={s.label} className="stat-pill" style={{ background:'#fff', border:`2px solid ${s.color}20`, borderRadius:'12px', padding:'0.75rem 1.25rem', minWidth:'120px', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
                        <div style={{ fontSize:'1.6rem', fontWeight:700, color:s.color }}>{s.val}</div>
                        <div style={{ fontSize:'0.78rem', color:'#6b7280', fontWeight:500 }}>{s.label}</div>
                    </div>
                ))}
            </div>

            {isLoading ? (
                <div className="loading-state"><div className="spinner"></div><p>Loading…</p></div>
            ) : filtered.length === 0 ? (
                <div className="empty-state-modern">
                    <i className="fas fa-shield-alt fa-3x" style={{ color:'#d1d5db', marginBottom:'1rem' }}></i>
                    <h3>No Authorization Sets</h3>
                    <p>Create your first auth set to define what users receive after login.</p>
                    <button className="btn-primary-modern" onClick={openCreate}><i className="fas fa-plus mr-2"></i>Create Auth Set</button>
                </div>
            ) : (
                <div className="resource-grid-modern">
                    {filtered.map(s => (
                        <div key={s._id} className="resource-card-modern authset-card">
                            <div className="card-header">
                                <div className="resource-avatar" style={{ background: s.status==='active' ? 'linear-gradient(135deg,#6366f1,#8b5cf6)' : '#e5e7eb' }}>
                                    <i className="fas fa-shield-alt" style={{ color: s.status==='active' ? '#fff' : '#9ca3af' }}></i>
                                </div>
                                <div className="resource-title">
                                    <h3>{s.name}</h3>
                                    <span className="resource-subtitle">{s.planId?.name || 'No plan linked'}</span>
                                </div>
                                <div className="card-actions">
                                    <button className="action-btn-icon" title="Duplicate" onClick={() => dupMutation.mutate(s._id)}>
                                        <i className="fas fa-copy"></i>
                                    </button>
                                    <button className="action-btn-icon edit" title="Edit" onClick={() => openEdit(s)}>
                                        <i className="fas fa-edit"></i>
                                    </button>
                                    <button className="action-btn-icon delete" title="Delete" onClick={() => { if(window.confirm(`Delete "${s.name}"?`)) deleteMutation.mutate(s._id); }}>
                                        <i className="fas fa-trash"></i>
                                    </button>
                                </div>
                            </div>

                            <div className="card-body">
                                <div className="status-badges" style={{ marginBottom:'0.75rem', display:'flex', gap:'0.4rem', flexWrap:'wrap' }}>
                                    <span className={`status-badge ${s.status==='active'?'active':'inactive'}`}>
                                        <span className="badge-dot"></span>{s.status?.toUpperCase()}
                                    </span>
                                    {(s.authMethods || []).slice(0,3).map(m => (AUTH_METHODS.some((x) => x.value === m) ? (
                                        <span key={m} className="status-badge" style={{ background:'#ede9fe', color:'#6d28d9', border:'1px solid #c4b5fd', fontSize:'0.7rem' }}>
                                            {methodLabel(m)}
                                        </span>
                                    ) : (
                                        <span key={m} className="status-badge" title="A set does not change this sign-in's plan" style={{ background:'#f8fafc', color:'#64748b', border:'1px dashed #cbd5e1', fontSize:'0.7rem' }}>
                                            {methodLabel(m)} — not applied
                                        </span>
                                    )))}
                                    {(s.authMethods||[]).length > 3 && (
                                        <span className="status-badge" style={{ background:'#f3f4f6', color:'#6b7280' }}>+{s.authMethods.length-3}</span>
                                    )}
                                </div>

                                {/* What the guest gets: the plan's own figures. */}
                                <div className="info-grid" style={{ gridTemplateColumns:'1fr 1fr' }}>
                                    <div className="info-item">
                                        <i className="fas fa-tachometer-alt"></i>
                                        <div><span className="info-label">Speed</span><span className="info-value" style={{ fontSize:'0.82rem' }}>{planSpeed(s.planId)}</span></div>
                                    </div>
                                    <div className="info-item">
                                        <i className="fas fa-clock"></i>
                                        <div><span className="info-label">Validity</span><span className="info-value">{planValidity(s.planId)}</span></div>
                                    </div>
                                    <div className="info-item">
                                        <i className="fas fa-mobile-alt"></i>
                                        <div><span className="info-label">Devices</span><span className="info-value">{s.planId?.sessionLimits?.maxConcurrentSessions || '—'}</span></div>
                                    </div>
                                    <div className="info-item">
                                        <i className="fas fa-database"></i>
                                        <div><span className="info-label">Data</span><span className="info-value">{planData(s.planId)}</span></div>
                                    </div>
                                    <div className="info-item">
                                        <i className="fas fa-code-branch"></i>
                                        <div><span className="info-label">Branch</span><span className="info-value">{s.branchId?.name || 'All branches'}</span></div>
                                    </div>
                                    <div className="info-item">
                                        <i className="fas fa-sort-amount-down"></i>
                                        <div><span className="info-label">Priority</span><span className="info-value">{s.priority || 0}</span></div>
                                    </div>
                                    {s.accessSchedule?.enabled && (
                                        <div className="info-item">
                                            <i className="fas fa-calendar-check"></i>
                                            <div><span className="info-label">Schedule</span><span className="info-value" style={{ color:'#f59e0b', fontWeight:600 }}>Active</span></div>
                                        </div>
                                    )}
                                </div>
                                {s.description && <p style={{ fontSize:'0.8rem', color:'#9ca3af', marginTop:'0.5rem', borderTop:'1px solid #f3f4f6', paddingTop:'0.5rem' }}>{s.description}</p>}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className="modal-overlay-modern" onClick={closeModal}>
                    <div className="modal-modern modal-large" onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div>
                                <h2>{editing ? 'Edit' : 'New'} Authorization Set</h2>
                                <p style={{ fontSize:'0.83rem', color:'#6b7280', marginTop:'0.2rem' }}>Define authentication trigger, internet plan, and network policies</p>
                            </div>
                            <button className="modal-close" onClick={closeModal}><i className="fas fa-times"></i></button>
                        </div>

                        {/* Tabs */}
                        <div className="modal-tabs">
                            {[['basic','Basic'], ['network','Network'], ['schedule','Schedule'], ['redirect','Portal']].map(([k,l])=>(
                                <button key={k} type="button" className={`modal-tab-btn ${tab===k?'active':''}`} onClick={()=>setTab(k)}>{l}</button>
                            ))}
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="modal-body-modern">

                                {tab === 'basic' && (
                                    <div className="form-section">
                                        <div className="form-grid">
                                            <div className="form-group-modern" style={{ gridColumn:'1/-1' }}>
                                                <label>Name <span style={{color:'red'}}>*</span></label>
                                                <input required value={form.name} onChange={e=>setF('name',e.target.value)} placeholder="e.g. Guest Room Basic" />
                                            </div>
                                            <div className="form-group-modern" style={{ gridColumn:'1/-1' }}>
                                                <label>Description</label>
                                                <textarea value={form.description} onChange={e=>setF('description',e.target.value)} rows={2} placeholder="Optional description" />
                                            </div>

                                            <div className="form-group-modern" style={{ gridColumn:'1/-1' }}>
                                                <label>Authentication Methods <span style={{fontSize:'0.78rem',color:'#6b7280'}}>(which login types activate this set)</span></label>
                                                <div style={{ display:'flex', flexWrap:'wrap', gap:'0.5rem', marginTop:'0.25rem' }}>
                                                    {AUTH_METHODS.map(m => (
                                                        <button key={m.value} type="button" aria-pressed={form.authMethods.includes(m.value)}
                                                            onClick={() => toggleMethod(m.value)}
                                                            style={{ padding:'0.4rem 0.85rem', borderRadius:'20px', fontSize:'0.8rem', cursor:'pointer', border:'2px solid', borderColor: form.authMethods.includes(m.value) ? '#6366f1' : '#e5e7eb', background: form.authMethods.includes(m.value) ? '#ede9fe' : '#f9fafb', color: form.authMethods.includes(m.value) ? '#4f46e5' : '#374151', fontWeight: form.authMethods.includes(m.value) ? 600 : 400, transition:'all 0.15s' }}>
                                                            {/* WhatsApp is a brand icon (fab), the rest solid (fas) */}
                                                            <i className={`${m.icon === 'fa-whatsapp' ? 'fab' : 'fas'} ${m.icon} mr-1`} style={{ fontSize:'0.75rem' }}></i>{m.label}
                                                        </button>
                                                    ))}
                                                    {form.authMethods.filter((m) => !AUTH_METHODS.some((x) => x.value === m)).map((m) => (
                                                        <button key={m} type="button" onClick={() => toggleMethod(m)}
                                                            title="A set does not change this sign-in's plan — click to take it off"
                                                            style={{ padding:'0.4rem 0.85rem', borderRadius:'20px', fontSize:'0.8rem', cursor:'pointer', border:'2px dashed #cbd5e1', background:'#f8fafc', color:'#64748b' }}>
                                                            {methodLabel(m)} — not applied <i className="fas fa-times ml-1"></i>
                                                        </button>
                                                    ))}
                                                </div>
                                                <p className="authset-help">
                                                    A guest who signs in this way gets this set's plan — before the branch's <em>Plan per login method</em>.
                                                    A room's own plan still comes first; a voucher always gives its own plan.
                                                </p>
                                            </div>

                                            <div className="form-group-modern" style={{ gridColumn:'1/-1' }}>
                                                <label>Internet Plan <span style={{fontSize:'0.78rem',color:'#6b7280'}}>(what the guest gets — speed, data, devices, validity)</span></label>
                                                <select required value={form.planId} onChange={e=>setF('planId',e.target.value)}>
                                                    <option value="">— Select Plan —</option>
                                                    {plans.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                                                </select>
                                            </div>

                                            {(admin?.role==='superadmin'||admin?.role==='tenant_admin') && branches.length > 0 && (
                                                <div className="form-group-modern">
                                                    <label>Branch</label>
                                                    <select value={form.branchId} onChange={e=>setF('branchId',e.target.value)}>
                                                        <option value="">All Branches</option>
                                                        {branches.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
                                                    </select>
                                                </div>
                                            )}

                                            <div className="form-group-modern">
                                                <label>Priority <span style={{fontSize:'0.78rem',color:'#6b7280'}}>(higher = evaluated first)</span></label>
                                                <input type="number" min={0} value={form.priority} onChange={e=>setF('priority',e.target.value)} />
                                            </div>

                                            <div className="form-group-modern">
                                                <label>Target User Types <span style={{fontSize:'0.78rem',color:'#6b7280'}}>(comma-separated)</span></label>
                                                <input value={form.targetUserTypes} onChange={e=>setF('targetUserTypes',e.target.value)} placeholder="guest, vip, staff" />
                                            </div>

                                            <div className="form-group-modern">
                                                <label>Status</label>
                                                <select value={form.status} onChange={e=>setF('status',e.target.value)}>
                                                    <option value="active">Active</option>
                                                    <option value="inactive">Inactive</option>
                                                </select>
                                            </div>
                                        </div>

                                    </div>
                                )}

                                {tab === 'network' && (
                                    <div className="form-section">
                                        <h3 className="section-title"><i className="fas fa-network-wired mr-2"></i>Network Policies</h3>
                                        <p className="authset-not-applied">
                                            <i className="fas fa-info-circle"></i> Saved with the set, but not sent to the gateway yet — at sign-in the set gives the guest its plan.
                                        </p>
                                        <div className="form-grid">
                                            <div className="form-group-modern">
                                                <label>VLAN ID <span style={{fontSize:'0.78rem',color:'#6b7280'}}>(optional — does not determine plan)</span></label>
                                                <input type="number" value={form.vlanId} onChange={e=>setF('vlanId',e.target.value)} placeholder="e.g. 4000" />
                                            </div>
                                            <div className="form-group-modern">
                                                <label>IP Pool Name <span style={{fontSize:'0.78rem',color:'#6b7280'}}>(RADIUS Framed-Pool)</span></label>
                                                <input value={form.ipPool} onChange={e=>setF('ipPool',e.target.value)} placeholder="e.g. pool-guest" />
                                            </div>
                                            <div className="form-group-modern">
                                                <label>DNS Profile</label>
                                                <input value={form.dnsProfile} onChange={e=>setF('dnsProfile',e.target.value)} placeholder="e.g. clean-dns" />
                                            </div>
                                            <div className="form-group-modern">
                                                <label>Firewall Policy Tag</label>
                                                <input value={form.firewallPolicy} onChange={e=>setF('firewallPolicy',e.target.value)} placeholder="e.g. guest-policy" />
                                            </div>
                                        </div>
                                        <div className="info-box-modern" style={{ background:'#fef3c7', border:'1px solid #fde68a', borderRadius:'8px', padding:'0.75rem 1rem', marginTop:'1rem' }}>
                                            <i className="fas fa-info-circle" style={{color:'#d97706',marginRight:'0.5rem'}}></i>
                                            <span style={{fontSize:'0.83rem',color:'#92400e'}}>VLAN is optional and <strong>never determines the Internet Plan</strong>. The Authorization Set determines the Plan.</span>
                                        </div>
                                    </div>
                                )}

                                {tab === 'schedule' && (
                                    <div className="form-section">
                                        <h3 className="section-title"><i className="fas fa-calendar-alt mr-2"></i>Access Schedule</h3>
                                        <div className="form-group-modern" style={{ marginBottom:'1rem' }}>
                                            <label style={{ display:'flex', alignItems:'center', gap:'0.5rem', cursor:'pointer' }}>
                                                <input type="checkbox" checked={form.accessSchedule.enabled} onChange={e=>setForm(f=>({...f,accessSchedule:{...f.accessSchedule,enabled:e.target.checked}}))} />
                                                Enable access schedule restrictions
                                            </label>
                                        </div>
                                        {form.accessSchedule.enabled && (
                                            <>
                                                <div className="form-group-modern" style={{ marginBottom:'1rem' }}>
                                                    <label>Allowed Days</label>
                                                    <div style={{ display:'flex', gap:'0.5rem', marginTop:'0.25rem' }}>
                                                        {DAYS.map(d=>(
                                                            <button key={d} type="button" onClick={()=>toggleDay(d)}
                                                                style={{ padding:'0.35rem 0.7rem', borderRadius:'8px', fontSize:'0.8rem', textTransform:'capitalize', cursor:'pointer', border:'2px solid', borderColor:form.accessSchedule.days.includes(d)?'#6366f1':'#e5e7eb', background:form.accessSchedule.days.includes(d)?'#ede9fe':'#f9fafb', color:form.accessSchedule.days.includes(d)?'#4f46e5':'#374151', fontWeight:form.accessSchedule.days.includes(d)?700:400 }}>
                                                                {d}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div className="form-grid">
                                                    <div className="form-group-modern">
                                                        <label>Start Time</label>
                                                        <input type="time" value={form.accessSchedule.startTime} onChange={e=>setForm(f=>({...f,accessSchedule:{...f.accessSchedule,startTime:e.target.value}}))} />
                                                    </div>
                                                    <div className="form-group-modern">
                                                        <label>End Time</label>
                                                        <input type="time" value={form.accessSchedule.endTime} onChange={e=>setForm(f=>({...f,accessSchedule:{...f.accessSchedule,endTime:e.target.value}}))} />
                                                    </div>
                                                    <div className="form-group-modern" style={{ gridColumn:'1/-1' }}>
                                                        <label>Timezone</label>
                                                        <input value={form.accessSchedule.timezone} onChange={e=>setForm(f=>({...f,accessSchedule:{...f.accessSchedule,timezone:e.target.value}}))} placeholder="Asia/Kolkata" />
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                {tab === 'redirect' && (
                                    <div className="form-section">
                                        <h3 className="section-title"><i className="fas fa-external-link-alt mr-2"></i>Portal & Redirect</h3>
                                        <p className="authset-not-applied">
                                            <i className="fas fa-info-circle"></i> Saved with the set, but not used by the portal yet — the branch's landing page and redirect apply.
                                        </p>
                                        <div className="form-grid">
                                            <div className="form-group-modern" style={{ gridColumn:'1/-1' }}>
                                                <label>Landing Page URL <span style={{fontSize:'0.78rem',color:'#6b7280'}}>(shown after login, if different from branch default)</span></label>
                                                <input value={form.landingPage} onChange={e=>setF('landingPage',e.target.value)} placeholder="https://yourhotel.com/welcome" />
                                            </div>
                                            <div className="form-group-modern" style={{ gridColumn:'1/-1' }}>
                                                <label>Post-Auth Redirect URL <span style={{fontSize:'0.78rem',color:'#6b7280'}}>(auto-redirect after login)</span></label>
                                                <input value={form.redirectUrl} onChange={e=>setF('redirectUrl',e.target.value)} placeholder="https://yourhotel.com" />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="modal-footer-modern">
                                <button type="button" className="btn-secondary-modern" onClick={closeModal}>Cancel</button>
                                <button type="submit" className="btn-primary-modern" disabled={saveMutation.isPending}>
                                    {saveMutation.isPending ? <><i className="fas fa-spinner fa-spin mr-2"></i>Saving…</> : <><i className="fas fa-save mr-2"></i>{editing ? 'Update' : 'Create'}</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
